import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAlerts } from '../../contexts/AlertContext';
import {
  MapPin, LogOut, User, TruckIcon, Search, Car, BarChart3, Eye, Settings,
  Bell, ChevronDown, Key, GitMerge, Menu, X, Download, UserCircle, Shield, TrendingUp, Package, AlertTriangle, Tags, Database, Crown, UserPlus,
Upload, FileText, } from 'lucide-react';
import { Tooltip } from './Tooltip';

// NavButton component with professional styling
interface NavButtonProps {
  icon: React.ElementType;
  label: string;
  route: string;
  tooltip: string;
  badge?: number;
  isActive: boolean;
  isPrimary?: boolean;
  onClick?: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({
  icon: Icon,
  label,
  route,
  tooltip,
  badge,
  isActive,
  isPrimary = false,
  onClick
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(route);
    }
  };

  // Primary CTA button style (Carrier Search)
  if (isPrimary) {
    return (
      <Tooltip content={tooltip}>
        <button
          onClick={handleClick}
          className={`
            relative flex items-center gap-2.5 px-5 py-2.5 rounded-xl
            text-sm font-semibold transition-all duration-300 ease-out
            ${isActive
              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30 scale-[1.02]'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 hover:scale-[1.02]'
            }
          `}
        >
          <Icon className="w-4 h-4" />
          <span>{label}</span>
          {isActive && (
            <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-8 h-1 bg-white/30 rounded-full" />
          )}
        </button>
      </Tooltip>
    );
  }

  // Secondary button style
  return (
    <Tooltip content={tooltip}>
      <button
        onClick={handleClick}
        className={`
          relative flex items-center gap-2 px-4 py-2.5 rounded-xl
          text-sm font-medium transition-all duration-300 ease-out
          ${isActive
            ? 'bg-blue-50 text-blue-700 border-2 border-blue-200 shadow-sm'
            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:text-blue-600 hover:-translate-y-0.5 hover:shadow-md'
          }
        `}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-600 rounded-r-full" />
        )}
        <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : ''}`} />
        <span>{label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="relative ml-1 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full shadow-sm">
            {badge > 99 ? '99+' : badge}
            <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75" />
          </span>
        )}
      </button>
    </Tooltip>
  );
};

// Mobile NavButton for the hamburger menu
const MobileNavButton: React.FC<NavButtonProps & { onClose: () => void }> = ({
  icon: Icon,
  label,
  route,
  badge,
  isActive,
  isPrimary,
  onClick,
  onClose
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(route);
    }
    onClose();
  };

  return (
    <button
      onClick={handleClick}
      className={`
        relative w-full flex items-center gap-4 px-5 py-4 rounded-xl
        text-base font-medium transition-all duration-200
        ${isPrimary
          ? isActive
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
          : isActive
            ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
            : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
        }
      `}
    >
      <Icon className={`w-5 h-5 ${isActive && !isPrimary ? 'text-blue-600' : ''}`} />
      <span className="flex-1 text-left">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="px-2.5 py-1 text-xs font-bold bg-red-500 text-white rounded-full shadow-sm">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {isActive && !isPrimary && (
        <span className="w-2 h-2 bg-blue-600 rounded-full" />
      )}
    </button>
  );
};

export const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { unreadCount } = useAlerts();
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile menu state (closed by default)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const moreMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Check if current path matches route
  const isActiveRoute = (route: string): boolean => {
    if (route === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/carrier/');
    }
    return location.pathname === route;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (moreMenuRef.current && !moreMenuRef.current.contains(target)) {
        setMoreMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
    };

    if (moreMenuOpen || userMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
  }, [moreMenuOpen, userMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMoreMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.company_name) return 'U';
    return user.company_name
      .split(' ')
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // Primary navigation items
  const primaryNavItems: Array<{
    icon: React.ElementType;
    label: string;
    route: string;
    tooltip: string;
    badge?: number;
    isPrimary?: boolean;
  }> = [
    {
      icon: Search,
      label: 'Search',
      route: '/carrier-search',
      tooltip: 'Search carriers by DOT, name, or MC number'
    },
    {
      icon: Shield,
      label: "Compare",
      route: "/carrier-compare",
      tooltip: "Compare carriers side-by-side"
    },
    {
      icon: FileText,
      label: "Screen",
      route: "/bulk-screening",
      tooltip: "Bulk carrier screening"
    },
    {
      icon: Upload,
      label: "Screen",
      route: "/bulk-screening",
      tooltip: "Bulk carrier screening"
    },
    {
      icon: Eye,
      label: 'Watchlist',
      route: '/monitored',
      tooltip: 'View and manage your monitored carriers'
    },
    {
      icon: Bell,
      label: 'Alerts',
      route: '/alerts',
      tooltip: 'View notifications and alerts',
      badge: unreadCount
    },
    {
      icon: BarChart3,
      label: 'Analytics',
      route: '/statistics',
      tooltip: 'Platform statistics and trends'
    },
    {
      icon: MapPin,
      label: 'Geo Intel',
      route: '/geographic',
      tooltip: 'Geographic intelligence and carrier density analysis',
    },
    {
      icon: Car,
      label: 'VIN Lookup',
      route: '/vin-lookup',
      tooltip: 'Search VINs and detect fraud'
    }
  ];

  // More dropdown items - dynamically include Super Admin based on user role
  const moreMenuItems = [
    // Super Admin link - only for super_admin role (or admin for now since we don't have super_admin type yet)
    ...(user?.userType === 'admin' || user?.userType === 'government' || user?.role === 'super_admin' ? [{
      icon: Crown,
      label: 'Super Admin',
      route: '/super-admin',
      tooltip: 'Platform administration - Users, Companies, Analytics',
      active: true,
      priority: true
}] : []),    ...(user?.userType === 'admin' || user?.userType === 'government' || user?.role === 'super_admin' ? [{      icon: UserPlus,      label: 'Registration Requests',      route: '/admin/registration-requests',      tooltip: 'Review pending user registration requests',      active: true,      priority: true
    }] : []),
    {
      icon: GitMerge,
      label: 'VIN Matches',
      route: '/vin-matches',
      tooltip: 'View VIN matching analysis',
      active: true
    },
    {
      icon: Settings,
      label: 'Alert Preferences',
      route: '/alert-preferences',
      tooltip: 'Configure your alert settings',
      active: true
    },
    { divider: true },
    { header: 'Coming Soon' },
    {
      icon: Key,
      label: 'API Keys',
      route: '/api-keys',
      tooltip: 'Create API keys for TMS integrations',
      comingSoon: true
    },
    {
      icon: GitMerge,
      label: 'Merge Carriers',
      route: '/merge',
      tooltip: 'Analyze carrier mergers and acquisitions',
      active: true
    },
    {
      icon: TrendingUp,
      label: 'Market Intelligence',
      route: '/market-intelligence',
      tooltip: 'Advanced carrier search by location, fleet size, insurance, and ratings',
      active: true
    },
    {
      icon: Package,
      label: 'Shipper Audit',
      route: '/shipper-audit',
      tooltip: 'Analyze shipper performance, carrier networks, and inspection patterns',
      active: true
    },
    {
      icon: AlertTriangle,
      label: 'DNU Analytics',
      route: '/dnu-analytics',
      tooltip: 'Do Not Use (DNU) compliance monitoring and analytics dashboard',
      active: true
    },
    {
      icon: Tags,
      label: 'Tags Management',
      route: '/tags-management',
      tooltip: 'Manage inspection tags and categorize by shipper (USPS, FedEx, UPS, Amazon)',
      active: true
    },
    {
      icon: Download,
      label: 'Export Data',
      route: '/export',
      tooltip: 'Export your data to CSV or PDF',
      comingSoon: true
    }
  ];

  return (
    <>
    <nav className="bg-gradient-to-r from-white via-white to-gray-50 border-b border-gray-200/80 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] sticky top-0 z-[9999] backdrop-blur-sm">
      {/* Subtle gradient accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo and Brand */}
          <div className="flex items-center flex-shrink-0">
            <div
              className="flex items-center cursor-pointer group"
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 rounded-xl blur-lg group-hover:bg-blue-500/30 transition-all duration-300" />
                <div className="relative flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-all duration-300 group-hover:scale-105">
                  <TruckIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Navigation - Center (hidden on mobile/tablet) */}
          {isAuthenticated && user && (
            <div className="hidden lg:flex items-center gap-2 ml-8">
              {primaryNavItems.map((item) => (
                <NavButton
                  key={item.route}
                  icon={item.icon}
                  label={item.label}
                  route={item.route}
                  tooltip={item.tooltip}
                  badge={item.badge}
                  isActive={isActiveRoute(item.route)}
                  isPrimary={item.isPrimary}
                />
              ))}

              {/* More Menu Dropdown */}
              <div className="relative" ref={moreMenuRef}>
                <Tooltip content="Additional features">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMoreMenuOpen(!moreMenuOpen);
                      setUserMenuOpen(false);
                    }}
                    className={`
                      flex items-center gap-2 px-4 py-2.5 rounded-xl
                      text-sm font-medium transition-all duration-300 ease-out
                      border
                      ${moreMenuOpen
                        ? 'bg-gray-100 text-gray-900 border-gray-300 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900'
                      }
                    `}
                  >
                    <span>More</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${moreMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                </Tooltip>

                {/* Dropdown Menu */}
                <div
                  className={`
                    absolute right-0 mt-3 w-72 bg-white rounded-2xl
                    shadow-xl shadow-gray-200/50 border border-gray-200/80
                    py-2 z-50 overflow-hidden
                    transition-all duration-300 ease-out origin-top-right
                    ${moreMenuOpen
                      ? 'opacity-100 scale-100 translate-y-0'
                      : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }
                  `}
                >
                  {moreMenuItems.map((item, index) => {
                    if ('divider' in item && item.divider) {
                      return <div key={`divider-${index}`} className="border-t border-gray-100 my-2" />;
                    }
                    if ('header' in item && item.header) {
                      return (
                        <div key={`header-${index}`} className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {item.header}
                        </div>
                      );
                    }

                    const menuItem = item as {
                      icon: React.ElementType;
                      label: string;
                      route: string;
                      tooltip: string;
                      active?: boolean;
                      comingSoon?: boolean;
                      priority?: boolean;
                    };

                    return (
                      <button
                        key={menuItem.route}
                        onClick={() => {
                          if (menuItem.comingSoon) {
                            alert(`${menuItem.label} feature coming soon! ${menuItem.tooltip}`);
                          } else {
                            navigate(menuItem.route);
                            setMoreMenuOpen(false);
                          }
                        }}
                        title={menuItem.tooltip}
                        className={`
                          w-full text-left px-4 py-3 text-sm flex items-center gap-3
                          transition-all duration-200
                          ${menuItem.comingSoon
                            ? 'text-gray-400 cursor-not-allowed hover:bg-gray-50'
                            : isActiveRoute(menuItem.route)
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600 cursor-pointer'
                          }
                        `}
                      >
                        <div className={`p-2 rounded-lg ${menuItem.comingSoon ? 'bg-gray-100' : isActiveRoute(menuItem.route) ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <menuItem.icon className={`w-4 h-4 ${isActiveRoute(menuItem.route) ? 'text-blue-600' : ''}`} />
                        </div>
                        <span className="flex-1">{menuItem.label}</span>
                        {menuItem.comingSoon && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full font-medium">
                            Soon
                          </span>
                        )}
                        {menuItem.priority && (
                          <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Right Side Actions */}
          {isAuthenticated && user && (
            <div className="flex items-center gap-3 ml-auto relative z-[10000]">
              {/* Admin Dashboard Button - Only for admin/government users - Hidden on small screens */}
              {(user.userType === 'admin' || user.userType === 'government' || user.role === 'super_admin') && (
                <Tooltip content="Admin Dashboard">
                  <button
                    onClick={() => navigate('/admin')}
                    className={`
                      hidden md:inline-flex items-center gap-2 px-4 py-2.5
                      rounded-xl text-sm font-semibold transition-all duration-300
                      ${isActiveRoute('/admin')
                        ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/30'
                        : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-purple-500/25'
                      }
                    `}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </button>
                </Tooltip>
              )}

              {/* Super Admin Button - Only for admin/government users */}
              {(user.userType === 'admin' || user.userType === 'government' || user.role === 'super_admin') && (
                <Tooltip content="Super Admin Dashboard">
                  <button
                    onClick={() => navigate('/super-admin')}
                    className={`
                      hidden md:inline-flex items-center gap-2 px-4 py-2.5
                      rounded-xl text-sm font-semibold transition-all duration-300
                      ${isActiveRoute('/super-admin')
                        ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg shadow-amber-500/30'
                        : 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/25'
                      }
                    `}
                  >
                    <Crown className="w-4 h-4" />
                    Super Admin
                  </button>
                </Tooltip>
              )}

              {/* User Profile Dropdown - Hidden on small screens */}
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setUserMenuOpen(!userMenuOpen);
                    setMoreMenuOpen(false);
                  }}
                  className={`
                    flex items-center gap-3 pl-3 pr-4 py-2 rounded-xl
                    border transition-all duration-300
                    ${userMenuOpen
                      ? 'bg-gray-50 border-gray-300 shadow-sm'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }
                  `}
                >
                  {/* User Avatar with Initials */}
                  <div className="relative">
                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/20">
                      <span className="text-sm font-bold text-white">{getUserInitials()}</span>
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                  </div>
                  <div className="hidden xl:block text-left">
                    <p className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">
                      {user.company_name}
                    </p>
                    <p className="text-xs text-gray-500 capitalize flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                      {user.userType}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown Menu */}
                <div
                  className={`
                    absolute right-0 mt-3 w-72 bg-white rounded-2xl
                    shadow-xl shadow-gray-200/50 border border-gray-200/80
                    py-2 z-50 overflow-hidden
                    transition-all duration-300 ease-out origin-top-right
                    ${userMenuOpen
                      ? 'opacity-100 scale-100 translate-y-0'
                      : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }
                  `}
                >
                  {/* User Info Header */}
                  <div className="px-4 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                        <span className="text-lg font-bold text-white">{getUserInitials()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{user.company_name}</p>
                        <p className="text-xs text-gray-500 capitalize flex items-center gap-1.5">
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                          {user.userType} account
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        alert('Profile Settings feature coming soon! Manage your account settings here.');
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-400 hover:bg-gray-50 flex items-center gap-3 cursor-not-allowed"
                    >
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <UserCircle className="w-4 h-4" />
                      </div>
                      <span className="flex-1">Profile Settings</span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full font-medium">Soon</span>
                    </button>

                    <button
                      onClick={() => {
                        alert('API Keys feature coming soon! Create API keys for TMS integrations.');
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-gray-400 hover:bg-gray-50 flex items-center gap-3 cursor-not-allowed"
                    >
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Key className="w-4 h-4" />
                      </div>
                      <span className="flex-1">API Keys</span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full font-medium">Soon</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-100 my-1" />

                  {/* Logout */}
                  <div className="py-2">
                    <button
                      onClick={handleLogout}
                      className="text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors rounded-lg mx-2 w-[calc(100%-16px)]"
                    >
                      <div className="p-2 bg-red-100 rounded-lg">
                        <LogOut className="w-4 h-4" />
                      </div>
                      <span className="font-medium">Sign out</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Menu Button - Show on small screens */}
              <button
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                }}
                className={`
                  lg:hidden p-2.5 rounded-xl transition-all duration-300
                  ${mobileMenuOpen
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
                aria-label="Toggle mobile menu"
              >
                <div className="relative w-6 h-6">
                  <Menu className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} />
                  <X className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>

      {/* Mobile menu - Enabled for responsive design - Outside nav to avoid stacking context issues */}
      {isAuthenticated && user && (
        <>
          {/* Backdrop - Always in DOM, hidden on desktop via lg:hidden */}
          <div
            className={`
              fixed inset-0 bg-black/50 transition-opacity duration-300 z-[9998] lg:hidden
              ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}
            onClick={() => {
              console.log('[Navbar] Backdrop clicked, closing menu');
              setMobileMenuOpen(false);
            }}
          />

          {/* Slide-out Menu - Always in DOM, positioned off-screen when closed */}
          <div
            className={`
              fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-[10000] lg:hidden
              transform transition-transform duration-300 ease-out overflow-y-auto
              ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
            `}
            style={{ willChange: 'transform' }}
          >
            {/* Mobile Menu Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <span className="text-lg font-bold text-white">{getUserInitials()}</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{user.company_name}</p>
                  <p className="text-xs text-gray-500 capitalize flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    {user.userType}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Navigation Items */}
            <div className="px-4 py-6 space-y-6">
              {/* Primary Navigation */}
              <div>
                <p className="px-2 mb-3 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Navigation
                </p>
                <div className="space-y-1">
                  {primaryNavItems.map((item) => (
                    <MobileNavButton
                      key={item.route}
                      icon={item.icon}
                      label={item.label}
                      route={item.route}
                      tooltip={item.tooltip}
                      badge={item.badge}
                      isActive={isActiveRoute(item.route)}
                      isPrimary={item.isPrimary}
                      onClose={() => setMobileMenuOpen(false)}
                    />
                  ))}
                </div>
              </div>

              {/* Tools Section */}
              <div>
                <p className="px-2 mb-3 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Tools
                </p>
                <div className="space-y-1">
                  {moreMenuItems.filter(item => !('divider' in item) && !('header' in item) && !(item as any).comingSoon).map((item) => {
                    const menuItem = item as {
                      icon: React.ElementType;
                      label: string;
                      route: string;
                      tooltip: string;
                    };
                    return (
                      <MobileNavButton
                        key={menuItem.route}
                        icon={menuItem.icon}
                        label={menuItem.label}
                        route={menuItem.route}
                        tooltip={menuItem.tooltip}
                        isActive={isActiveRoute(menuItem.route)}
                        onClose={() => setMobileMenuOpen(false)}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Admin - only for admin users */}
              {(user.userType === 'admin' || user.userType === 'government' || user.role === 'super_admin') && (
                <div>
                  <p className="px-2 mb-3 text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Administration
                  </p>
                  <MobileNavButton
                    icon={Shield}
                    label="Admin Dashboard"
                    route="/admin"
                    tooltip="Admin Dashboard"
                    isActive={isActiveRoute('/admin')}
                    onClose={() => setMobileMenuOpen(false)}
                  />
                  <MobileNavButton
                    icon={UserPlus}
                    label="Registration Requests"
                    route="/admin/registration-requests"
                    tooltip="Review pending registrations"
                    isActive={isActiveRoute("/admin/registration-requests")}
                    onClose={() => setMobileMenuOpen(false)}
                  />
                </div>
              )}

              {/* Coming Soon Section */}
              <div>
                <p className="px-2 mb-3 text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Coming Soon
                </p>
                <div className="space-y-1 opacity-60">
                  {moreMenuItems.filter(item => (item as any).comingSoon).map((item) => {
                    const menuItem = item as {
                      icon: React.ElementType;
                      label: string;
                      route: string;
                      tooltip: string;
                      priority?: boolean;
                    };
                    return (
                      <button
                        key={menuItem.route}
                        onClick={() => alert(`${menuItem.label} feature coming soon! ${menuItem.tooltip}`)}
                        className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-base font-medium text-gray-400 hover:bg-gray-50"
                      >
                        <menuItem.icon className="w-5 h-5" />
                        <span className="flex-1 text-left">{menuItem.label}</span>
                        {menuItem.priority && (
                          <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Account Section */}
              <div className="border-t border-gray-100 pt-6">
                <p className="px-2 mb-3 text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Account
                </p>
                <div className="space-y-1">
                  <button
                    onClick={() => {
                      alert('Profile Settings feature coming soon!');
                    }}
                    className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-base font-medium text-gray-400 hover:bg-gray-50"
                  >
                    <UserCircle className="w-5 h-5" />
                    <span className="flex-1 text-left">Profile Settings</span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">Soon</span>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
